import { useState } from "react";
import { Pencil2Icon, TrashIcon, FileTextIcon } from "@radix-ui/react-icons";
import EditPurchaseForm from "../forms/EditPurchaseForm";
import { ResponsiveDialogWrapper } from "../ResponsiveDialogWrapper";
import EditListingForm from "../forms/EditListingForm";
import DeleteListingForm from "../DeleteListingForm";
import { ListingWithItems, PurchaseWithItems } from "@/types";
import { DbItemSelect } from "@server/db/schema";
import { cn } from "@/lib/utils";
import DialogWrapper from "../DialogWrapper";

type FFF =
  | {
      data: ListingWithItems;
      type: "listings";
    }
  | {
      data: PurchaseWithItems;
      type: "purchases";
    }
  | {
      data: DbItemSelect;
      type: "items";
    };

export default function CellEditButton({ data, type }: FFF) {
  const [notesOpen, setNotesOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (type === "items") return;

  const dataItems = data.items;

  const allowDelete =
    type === "listings"
      ? Boolean(dataItems.filter((i) => i.listingId))
      : Boolean(dataItems.filter((i) => i.listingId === null).length);

  const formToShow = (): React.ReactNode => {
    switch (type) {
      case "purchases":
        return (
          <EditPurchaseForm
            data={data as PurchaseWithItems}
            closeModal={() => setEditOpen(false)}
          />
        );
      case "listings":
        return (
          <EditListingForm
            data={data as ListingWithItems}
            closeModal={() => setEditOpen(false)}
          />
        );
      default:
        break;
    }
  };

  const description = () => {
    switch (type) {
      case "purchases":
        return data.purchaseName;
      case "listings":
        return data.listingName;
      default:
        break;
    }
  };

  const notes = () => {
    switch (type) {
      case "purchases":
        return data.purchaseNotes;
      case "listings":
        return data.listingNotes;
      default:
        return null;
    }
  };

  return (
    <div className="flex justify-end items-start gap-x-1">
      {notes() && (
        <DialogWrapper
          isButton
          title={description() || "Notes"}
          stopPropagation={true}
          open={notesOpen}
          setOpen={setNotesOpen}
          trigger={
            <FileTextIcon className="w-4 h-4 p-0.5 text-primary-foreground/60 hover:text-primary-foreground" />
          }
          content={<div className="">{notes()}</div>}
        />
      )}

      <DialogWrapper
        isButton
        title={`Edit ${type}`}
        description={description()}
        content={formToShow()}
        stopPropagation={true}
        trigger={
          <Pencil2Icon className="h-4 w-4 p-0.5 text-primary-foreground/60 hover:text-primary-foreground" />
        }
        open={editOpen}
        setOpen={setEditOpen}
      />

      <DialogWrapper
        isButton
        title={`Delete ${type}`}
        description={description()}
        stopPropagation={true}
        content={
          type === "listings" ? (
            <DeleteListingForm
              data={data as ListingWithItems}
              type="listings"
              setOpen={setDeleteOpen}
              allowDelete={allowDelete}
            />
          ) : (
            <DeleteListingForm
              data={data as PurchaseWithItems}
              type="purchases"
              setOpen={setDeleteOpen}
              allowDelete={allowDelete}
            />
          )
        }
        trigger={
          <TrashIcon className={cn("h-4 w-4 p-0.5 text-primary-foreground/60 hover:text-primary-foreground")} />
        }
        open={deleteOpen}
        setOpen={setDeleteOpen}
      />
    </div>
  );
}
